import React, { useContext } from 'react';

import PropTypes from 'prop-types';
import MessageDetailsShape from 'app/shapes/MessageDetailsShape';
import DialogsContext from 'app/contexts/DialogsContext';

import { AVAILABLE_BALANCE_TAG } from 'app/api/sharedApiTags';
import useInvalidateCacheTagsDispatcher from 'app/hooks/useInvalidateCacheTagsDispatcher';
import { useCreatePaymentMutation } from 'app/features/mail/api/mailApi';

import { useDispatch } from 'react-redux';
import { showSnackbarMessage } from 'app/slices/snackbarMessageSlice';

import {
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { toCurrency } from 'app/helpers/currencyHelpers';

const ConfirmPaymentDialog = ({
  messageDetails,
  title,
  fetchReceivedMessageDetails,
  setSelectedMessageDetails,
}) => {
  const { closeDialog } = useContext(DialogsContext);
  const [createPayment] = useCreatePaymentMutation();
  const dispatch = useDispatch();

  const { dispatchInvalidateCacheTagsEvent } = useInvalidateCacheTagsDispatcher();
  const handlePayment = () => {
    // The server will return a toast response.
    createPayment({ messageId: messageDetails.id })
      .then(() => {
        dispatchInvalidateCacheTagsEvent([AVAILABLE_BALANCE_TAG]);
        // The message detail is loaded via a lazy query in the container, so we need to refetch it and set it.
        fetchReceivedMessageDetails({ messageId: messageDetails.id })
          .then((result) => {
            if (result.error) {
              dispatch(showSnackbarMessage({
                message: 'There was an error loading your message. Please try again later.',
                severity: 'error',
              }));
              setSelectedMessageDetails(null);
            } else {
              setSelectedMessageDetails(result.data);
            }
          });
        closeDialog();
      });
  };

  return (
    <>
      <DialogTitle>
        {title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        Click &apos;Continue&apos; to send payment.
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <strong>To:</strong>
          </Grid>
          <Grid item xs={6}>
            {messageDetails.sender.login}
          </Grid>
          <Grid item xs={6}>
            <strong>Subject:</strong>
          </Grid>
          <Grid item xs={6}>
            {messageDetails.subject}
          </Grid>
          <Grid item xs={6}>
            <strong>Payment Amount:</strong>
          </Grid>
          <Grid item xs={6}>
            {toCurrency(messageDetails.paymentAmount)}
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePayment}
            >
              Continue
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={closeDialog}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  );
};

ConfirmPaymentDialog.defaultProps = {
  title: 'Confirm Payment',
};

ConfirmPaymentDialog.propTypes = {
  title: PropTypes.string,
  messageDetails: MessageDetailsShape.isRequired,
  fetchReceivedMessageDetails: PropTypes.func.isRequired,
  setSelectedMessageDetails: PropTypes.func.isRequired,
};

export default ConfirmPaymentDialog;
